import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import AdminLayout from '../../components/AdminLayout';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const CONTRACTOR_SERVICES = [
  { value: 'builders', label: 'Builders' },
  { value: 'tilers', label: 'Tilers' },
  { value: 'roofers', label: 'Roofers' },
  { value: 'plumbers', label: 'Plumbers' },
  { value: 'electricians', label: 'Electricians' },
  { value: 'architects', label: 'Architects' },
  { value: 'painters', label: 'Painters' },
  { value: 'landscapers', label: 'Landscapers' },
  { value: 'carpenters', label: 'Carpenters' }
];

const AdminContractors = () => {
  const [contractors, setContractors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedContractor, setSelectedContractor] = useState(null);
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [verificationNotes, setVerificationNotes] = useState('');
  const [verificationAction, setVerificationAction] = useState('');

  useEffect(() => {
    fetchContractors();
  }, [statusFilter]);

  const fetchContractors = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      
      let url = `${API}/admin/contractors`;
      if (statusFilter !== 'all') {
        url += `?verificationStatus=${statusFilter}`;
      }

      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setContractors(response.data);
    } catch (error) {
      console.error('Error fetching contractors:', error);
      toast.error('Failed to load contractors');
    } finally {
      setLoading(false);
    }
  };

  const handleVerification = async (action) => {
    if (!selectedContractor) return;

    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(
        `${API}/admin/contractors/${selectedContractor.id}/verify?status=${action}&notes=${encodeURIComponent(verificationNotes)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Contractor ${action === 'approved' ? 'approved' : 'rejected'} successfully`);
      setShowVerifyModal(false);
      setSelectedContractor(null);
      setVerificationNotes('');
      fetchContractors();
    } catch (error) {
      console.error('Error verifying contractor:', error);
      toast.error('Failed to update verification status');
    }
  };

  const openVerifyModal = (contractor, action) => {
    setSelectedContractor(contractor);
    setVerificationAction(action);
    setVerificationNotes('');
    setShowVerifyModal(true);
  };

  const getServiceLabel = (value) => {
    const service = CONTRACTOR_SERVICES.find(s => s.value === value);
    return service ? service.label : value;
  };

  const getStatusBadge = (status, isVerified) => {
    if (isVerified) {
      return <Badge className="bg-blue-100 text-blue-700"><i className="fas fa-check-circle mr-1"></i> Verified</Badge>;
    }
    switch (status) {
      case 'pending':
        return <Badge className="bg-yellow-100 text-yellow-700"><i className="fas fa-clock mr-1"></i> Pending</Badge>;
      case 'approved':
        return <Badge className="bg-green-100 text-green-700"><i className="fas fa-check mr-1"></i> Approved</Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-700"><i className="fas fa-times mr-1"></i> Rejected</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getSubscriptionBadge = (status) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-emerald-100 text-emerald-700">Active</Badge>;
      case 'trial':
        return <Badge className="bg-purple-100 text-purple-700">Trial</Badge>;
      default:
        return <Badge className="bg-gray-100 text-gray-600">Inactive</Badge>;
    }
  };

  const filteredContractors = contractors.filter(c => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      c.businessName?.toLowerCase().includes(query) ||
      c.contractorName?.toLowerCase().includes(query) ||
      c.email?.toLowerCase().includes(query) ||
      c.location?.toLowerCase().includes(query)
    );
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-ZA');
  };

  return (
    <AdminLayout>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Contractor Management</h1>
            <p className="text-gray-600">Verify and manage contractor profiles</p>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="p-4">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm text-gray-500">Total Contractors</p>
                  <p className="text-2xl font-bold">{contractors.length}</p>
                </div>
                <div className="p-3 bg-emerald-100 rounded-full">
                  <i className="fas fa-hard-hat text-emerald-600 text-xl"></i>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm text-gray-500">Pending Verification</p>
                  <p className="text-2xl font-bold text-yellow-600">
                    {contractors.filter(c => c.verificationStatus === 'pending').length}
                  </p>
                </div>
                <div className="p-3 bg-yellow-100 rounded-full">
                  <i className="fas fa-clock text-yellow-600 text-xl"></i>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm text-gray-500">Verified</p>
                  <p className="text-2xl font-bold text-blue-600">
                    {contractors.filter(c => c.isVerified).length}
                  </p>
                </div>
                <div className="p-3 bg-blue-100 rounded-full">
                  <i className="fas fa-check-circle text-blue-600 text-xl"></i>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm text-gray-500">Active Subscriptions</p>
                  <p className="text-2xl font-bold text-emerald-600">
                    {contractors.filter(c => c.subscriptionStatus === 'active' || c.subscriptionStatus === 'trial').length}
                  </p>
                </div>
                <div className="p-3 bg-emerald-100 rounded-full">
                  <i className="fas fa-crown text-emerald-600 text-xl"></i>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <Input
                  placeholder="Search by name, email, or location..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  data-testid="contractor-search"
                />
              </div>
              <div className="w-full md:w-48">
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger data-testid="status-filter">
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button variant="outline" onClick={fetchContractors}>
                <i className="fas fa-sync-alt mr-2"></i> Refresh
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Contractors List */}
        {loading ? (
          <div className="text-center py-12">
            <i className="fas fa-spinner fa-spin text-4xl text-emerald-600"></i>
            <p className="mt-4 text-gray-600">Loading contractors...</p>
          </div>
        ) : filteredContractors.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <i className="fas fa-hard-hat text-6xl text-gray-300 mb-4"></i>
              <p className="text-gray-500">No contractors found</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredContractors.map((contractor) => (
              <Card key={contractor.id} className="hover:shadow-md transition-shadow" data-testid={`contractor-row-${contractor.id}`}>
                <CardContent className="p-4">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center">
                          <i className="fas fa-hard-hat text-emerald-600"></i>
                        </div>
                        <div>
                          <h3 className="font-semibold flex items-center gap-2">
                            {contractor.businessName}
                            {getStatusBadge(contractor.verificationStatus, contractor.isVerified)}
                            {getSubscriptionBadge(contractor.subscriptionStatus)}
                          </h3>
                          <p className="text-sm text-gray-500">{contractor.contractorName} • {contractor.email}</p>
                        </div>
                      </div>
                      <div className="mt-3 flex flex-wrap gap-4 text-sm text-gray-600">
                        <span><i className="fas fa-map-marker-alt mr-1"></i> {contractor.location}</span>
                        <span><i className="fas fa-phone mr-1"></i> {contractor.contactNumber}</span>
                        <span><i className="fas fa-star text-yellow-400 mr-1"></i> {contractor.averageRating?.toFixed(1) || '0.0'}</span>
                        <span><i className="fas fa-calendar mr-1"></i> Joined: {formatDate(contractor.createdAt)}</span>
                      </div>
                      <div className="mt-2 flex flex-wrap gap-1">
                        {contractor.services?.slice(0, 4).map(s => (
                          <Badge key={s} variant="outline" className="text-xs">{getServiceLabel(s)}</Badge>
                        ))}
                        {contractor.services?.length > 4 && (
                          <Badge variant="outline" className="text-xs">+{contractor.services.length - 4}</Badge>
                        )}
                      </div>
                    </div>
                    
                    {/* Document Status */}
                    <div className="flex flex-col gap-1 text-xs">
                      <span className={contractor.idDocumentUrl ? 'text-green-600' : 'text-gray-400'}>
                        <i className={`fas ${contractor.idDocumentUrl ? 'fa-check-circle' : 'fa-circle'} mr-1`}></i>
                        ID Document
                      </span>
                      <span className={contractor.cipcDocumentUrl ? 'text-green-600' : 'text-gray-400'}>
                        <i className={`fas ${contractor.cipcDocumentUrl ? 'fa-check-circle' : 'fa-circle'} mr-1`}></i>
                        CIPC
                      </span>
                      <span className={contractor.tradeCertificateUrl ? 'text-green-600' : 'text-gray-400'}>
                        <i className={`fas ${contractor.tradeCertificateUrl ? 'fa-check-circle' : 'fa-circle'} mr-1`}></i>
                        Trade Cert
                      </span>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2">
                      {contractor.verificationStatus === 'pending' && (
                        <>
                          <Button 
                            size="sm" 
                            className="bg-green-600 hover:bg-green-700"
                            onClick={() => openVerifyModal(contractor, 'approved')}
                            data-testid={`approve-btn-${contractor.id}`}
                          >
                            <i className="fas fa-check mr-1"></i> Approve
                          </Button>
                          <Button 
                            size="sm" 
                            variant="destructive"
                            onClick={() => openVerifyModal(contractor, 'rejected')}
                            data-testid={`reject-btn-${contractor.id}`}
                          >
                            <i className="fas fa-times mr-1"></i> Reject
                          </Button>
                        </>
                      )}
                      {contractor.verificationStatus === 'rejected' && (
                        <Button 
                          size="sm" 
                          className="bg-green-600 hover:bg-green-700"
                          onClick={() => openVerifyModal(contractor, 'approved')}
                        >
                          <i className="fas fa-check mr-1"></i> Approve
                        </Button>
                      )}
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => window.open(`/contractor/${contractor.id}`, '_blank')}
                      >
                        <i className="fas fa-external-link-alt mr-1"></i> View
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Verification Modal */}
        <Dialog open={showVerifyModal} onOpenChange={setShowVerifyModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {verificationAction === 'approved' ? 'Approve Contractor' : 'Reject Contractor'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-600">
                  {verificationAction === 'approved' 
                    ? `Are you sure you want to approve "${selectedContractor?.businessName}"?`
                    : `Are you sure you want to reject "${selectedContractor?.businessName}"?`
                  }
                </p>
              </div>
              <div>
                <Label>Notes {verificationAction === 'rejected' && '(Required for rejection)'}</Label>
                <Textarea
                  value={verificationNotes}
                  onChange={(e) => setVerificationNotes(e.target.value)}
                  placeholder={verificationAction === 'rejected' 
                    ? 'Please provide a reason for rejection...'
                    : 'Optional notes...'}
                  rows={3}
                  data-testid="verification-notes"
                />
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setShowVerifyModal(false)}>
                  Cancel
                </Button>
                <Button
                  className={verificationAction === 'approved' ? 'bg-green-600 hover:bg-green-700' : ''}
                  variant={verificationAction === 'rejected' ? 'destructive' : 'default'}
                  onClick={() => handleVerification(verificationAction)}
                  disabled={verificationAction === 'rejected' && !verificationNotes.trim()}
                  data-testid="confirm-verification-btn"
                >
                  {verificationAction === 'approved' ? 'Approve' : 'Reject'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminContractors;
